/**
 * LaTeX command → Unicode symbol. For rendering that doesn't run a math engine:
 * relation/operator commands like `\rightarrow`, `\lt`, `\geq` are translated to
 * the plain symbol instead of showing up verbatim. Bidirectional arrows map to
 * `⇆` rather than the textbook `↔`.
 *
 * @type {Record<string, string>}
 */
const SYMBOLS = {
  // Arrows
  leftrightarrow: "⇆",
  leftrightarrows: "⇆",
  rightleftarrows: "⇆",
  rightleftharpoons: "⇆",
  rightarrow: "→",
  leftarrow: "←",
  Rightarrow: "⇒",
  Leftarrow: "⇐",
  Leftrightarrow: "⇔",
  to: "→",
  gets: "←",
  // Comparisons
  leq: "≤",
  geq: "≥",
  neq: "≠",
  le: "≤",
  ge: "≥",
  ne: "≠",
  ll: "≪",
  gg: "≫",
  lt: "<",
  gt: ">",
  approx: "≈",
  equiv: "≡",
  sim: "∼",
  propto: "∝",
  // Operators
  times: "×",
  div: "÷",
  pm: "±",
  mp: "∓",
  cdot: "·",
  // Misc
  infty: "∞",
};

// Longest command first, so `\leq` can't be claimed by `\le` (the letter-boundary
// lookahead below already guards this, but ordering is cheap insurance).
const NAMES = Object.keys(SYMBOLS)
  .toSorted((a, b) => b.length - a.length)
  .join("|");

// `$\rightarrow$` — an inline-math span holding one command (inner whitespace ok).
// Strips the `$` delimiters along with the command.
const WRAPPED = new RegExp(String.raw`\$\s*\\(${NAMES})\s*\$`, "g");

// A bare `\rightarrow` / `\lt`. The lookahead stops at a letter so `\total`
// isn't read as `\to` + "tal".
const BARE = new RegExp(String.raw`\\(${NAMES})(?![a-zA-Z])`, "g");

// An inline-math span holding a relation but no command — `$< 30$`, `$> 140/90$`.
// Match any single-line `$…$` span (`WRAPPED` above took the single-command ones)…
const MATH_SPAN = /\$([^$\n]+?)\$/g;
// …but only unwrap it when the content is purely mathematical: digits, whitespace,
// relation/operator punctuation and commands — never prose. Leaves a lone `$`,
// currency like `$5`, or `$5 a day$` untouched.
const MATH_CONTENT = /^(?:\\[a-zA-Z]+|[\d\s.,;:/()%+*=<>≤≥≠≈±×÷·−–—∞…-])+$/;
// Require an actual math token, so a span of only punctuation isn't unwrapped.
const HAS_MATH = /[\d<>=≤≥≠]|\\[a-zA-Z]/;

// `\lt` / `\gt` translate to a literal `<` / `>`. When the result is injected
// into HTML, escape only that substituted bracket — never the surrounding input,
// which may legitimately contain markup like <b> or <br>.
const HTML_ESCAPES = {"<": "&lt;", ">": "&gt;"};

function toSymbol(cmd, escapeHtml) {
  const symbol = SYMBOLS[cmd];

  return escapeHtml ? (HTML_ESCAPES[symbol] ?? symbol) : symbol;
}

/**
 * Replace basic LaTeX relation/operator commands with their Unicode symbol.
 *
 * @param {string} text input text
 * @param {{ escapeHtml?: boolean }} [options] `escapeHtml` HTML-escapes the `<`/`>`
 *   produced by `\lt`/`\gt`, for callers that inject the string into HTML directly.
 *   Brackets already in the input are left alone, so existing markup survives.
 * @returns {string} `$\rightarrow$`, `\lt`, … turned into `→`, `<` (or `&lt;` when
 *   `escapeHtml` is set)
 */
export function replaceLatexSymbols(text, {escapeHtml = false} = {}) {
  return (text ?? "")
    .replaceAll(WRAPPED, (_whole, cmd) => toSymbol(cmd, escapeHtml))
    .replaceAll(MATH_SPAN, (whole, inner) => {
      const trimmed = inner.trim();

      return MATH_CONTENT.test(trimmed) && HAS_MATH.test(trimmed) ? trimmed : whole;
    })
    .replaceAll(BARE, (_whole, cmd) => toSymbol(cmd, escapeHtml));
}
